import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Sparkles, Send, Bot, User as UserIcon, Headset } from "lucide-react";

export default function TicketDetail() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get(`/tickets/${ticketId}`).then((r) => setTicket(r.data));
  useEffect(() => { load(); }, [ticketId]); // eslint-disable-line

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/tickets/${ticketId}/messages`, { body: reply });
      setReply("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const askAi = async () => {
    setAiLoading(true);
    try {
      await api.post(`/tickets/${ticketId}/ai-assist`);
      toast.success("Assistente IA respondeu");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setAiLoading(false); }
  };

  if (!ticket) return <div className="text-zinc-500">Carregando…</div>;

  const iconFor = (role) => role === "ai" ? Bot : role === "admin" ? Headset : UserIcon;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ticket-detail-page">
      <Link to="/dashboard/tickets" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// ticket #{ticket.ticket_id.slice(-6)}</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tighter">{ticket.subject}</h1>
          <div className="text-xs font-mono text-zinc-500 mt-1">{ticket.department} · prioridade {ticket.priority} · status {ticket.status}</div>
        </div>
        <Button onClick={askAi} disabled={aiLoading} variant="outline" className="rounded-sm border-white/20 hover:bg-white hover:text-black" data-testid="ai-assist-btn">
          <Sparkles className="h-4 w-4 mr-2" /> {aiLoading ? "Consultando IA…" : "Pedir ajuda à IA"}
        </Button>
      </div>

      <div className="space-y-3">
        {ticket.messages.map((m, idx) => {
          const Icon = iconFor(m.author_role);
          const isSelf = m.author_role === "client";
          const badgeCls = m.author_role === "ai"
            ? "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30"
            : m.author_role === "admin"
            ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
            : "bg-zinc-500/20 text-zinc-300 border-white/10";
          return (
            <div key={idx} className={`border border-white/10 bg-[#121214] p-4 rounded-sm ${isSelf ? "border-l-2 border-l-white" : ""}`} data-testid={`ticket-msg-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-mono">
                  <Icon className="h-3.5 w-3.5" />
                  <span>{m.author_name}</span>
                  <span className={`px-2 py-0.5 border rounded-sm uppercase text-[10px] tracking-widest ${badgeCls}`}>{m.author_role}</span>
                </div>
                <span className="text-xs font-mono text-zinc-500">{fmtDate(m.created_at)}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-zinc-200">{m.body}</p>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border border-white/10 bg-[#121214] p-4 space-y-3" data-testid="ticket-reply-form">
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">// nova mensagem</div>
        <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} className="rounded-sm bg-[#09090B] border-white/10" placeholder="Escreva sua resposta…" data-testid="ticket-reply-input" />
        <div className="flex justify-end">
          <Button type="submit" disabled={sending || !reply.trim()} className="rounded-sm bg-white text-black hover:bg-zinc-200 btn-sharp" data-testid="ticket-reply-submit">
            <Send className="h-4 w-4 mr-2" /> {sending ? "Enviando…" : "Enviar"}
          </Button>
        </div>
      </form>
    </div>
  );
}
